import { cn } from "@/lib/utils";

/* The button's two sizes: a field and the control beside it share a line and
   have to agree about how tall it is. The house modal's field has its line to
   itself, and the design draws it taller and rounder; a popover's a step under. */
const SIZE = {
  default: "h-8 px-3 text-sm",
  dense: "h-7 px-2 text-[11.5px]",
  roomy: "h-[38px] rounded-md px-[11px] text-[13px]",
  snug: "h-[34px] rounded-inset px-2.5 text-[12px] placeholder:text-[11px]",
} as const;

const AREA = {
  default: "px-3 py-2 text-sm leading-relaxed",
  dense: "px-2 py-1.5 text-[11.5px] leading-relaxed",
  roomy: "rounded-md px-[11px] py-2 text-[13px] leading-relaxed",
  snug: "rounded-md px-3 py-2.5 text-detail leading-[1.55]",
} as const;

export type FieldSize = keyof typeof SIZE;

/* The card's radius, as the button's is: `rounded-xl` is 30px here, nearly
   twice the panel's. `border-input` because a field says where it can be typed
   in. Written in, it takes the design's violet ring; empty, a placeholder in
   the muted grey. The design's lighter caption colour is 3.7:1 on the card,
   under the 4.5:1 AA asks of text. The held look keys on the `readonly` attribute,
   since `:read-only` also matches a select and a disabled field. */
const SHARED =
  "w-full rounded-lg border border-input bg-card text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 [&[readonly]]:cursor-default [&[readonly]]:bg-muted/40 [&[readonly]]:text-foreground/80 disabled:cursor-not-allowed disabled:opacity-50";

export function fieldChrome(size: FieldSize = "default"): string {
  return cn(SHARED, SIZE[size]);
}

/** No fixed height: it sets its padding but not its line count. */
export function areaChrome(size: FieldSize = "default"): string {
  return cn(SHARED, AREA[size]);
}

/**
 * A writing box that reads as the words it holds: no ground and no edge until
 * the pointer rests on it, the design's amber edit ring once it is being
 * written in, and as many lines as the words take. The type is the caller's,
 * since the words are drawn as whatever they are, a claim line or a heading.
 */
export function inlineAreaChrome(): string {
  return "-mx-1.5 block w-[calc(100%+12px)] cursor-text resize-none rounded-sm border-0 bg-transparent px-1.5 py-px [field-sizing:content] underline decoration-border decoration-dotted underline-offset-[3px] transition-[background-color,box-shadow] hover:bg-card hover:ring-1 hover:ring-warning-border hover:ring-inset hover:decoration-warning focus-visible:bg-card focus-visible:no-underline focus-visible:ring-[1.5px] focus-visible:ring-warning focus-visible:ring-inset focus-visible:outline-none disabled:cursor-not-allowed";
}

/** A line's look and ring that wraps and grows with its words: a definition edited in its row. */
export function inlineLineAreaChrome(): string {
  return cn(inlineFieldChrome(), "block resize-none [field-sizing:content]");
}

/** One line drawn as its words until written in, then the field's violet ring. */
export function inlineFieldChrome(): string {
  return "-mx-1.5 w-[calc(100%+12px)] min-w-0 rounded-sm border-0 bg-transparent px-1.5 py-px text-foreground transition-[background-color,box-shadow] hover:bg-card hover:ring-1 hover:ring-border hover:ring-inset focus-visible:bg-card focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset focus-visible:outline-none disabled:cursor-not-allowed";
}

/** The names themselves, so a test can assert the set rather than a sample of it. */
export const FIELD_SIZES = Object.keys(SIZE) as readonly FieldSize[];
